import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShoppingCart, Package, Truck, CheckCircle, XCircle, Clock, Search, Filter, DollarSign, RotateCcw, MessageSquare, ChevronRight, X } from 'lucide-react';
import { authService } from '../services/auth';
import apiClient from '../services/api';
import { toast } from 'sonner';

export default function OrderManagementPage() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [stats, setStats] = useState({ total: 0, pending: 0, fulfilled: 0, revenue: 0, today: 0 });
  const [note, setNote] = useState('');

  useEffect(() => { loadData(); }, [statusFilter]);

  const loadData = async () => {
    try {
      const [profile, ordersRes, statsRes] = await Promise.all([
        authService.getProfile(),
        apiClient.get('/api/orders', { params: { status: statusFilter !== 'all' ? statusFilter : undefined, search } }),
        apiClient.get('/api/orders/stats')
      ]);
      setUser(profile.member);
      setOrders(ordersRes.data.orders || []);
      setStats(statsRes.data || stats);
    } catch (err) {
      setOrders([
        { id: 'ORD-2026-001', customer: { name: 'Sarah Chen', email: 'sarah@example.com' }, status: 'processing', total: 249.98, items: [{ title: 'Wireless Headphones', qty: 1, price: 149.99 }, { title: 'USB-C Cable', qty: 2, price: 49.99 }], payment: { method: 'card', last4: '4242' }, created_at: '2026-03-06T10:30:00Z', shipping: { method: 'Express', address: '123 Main St, San Francisco, CA 94102' } },
        { id: 'ORD-2026-002', customer: { name: 'Agent-7X9K (Moltbook)', email: null }, status: 'pending', total: 89.99, items: [{ title: 'Smart Sensor Kit', qty: 1, price: 89.99 }], payment: { method: 'usdc', wallet: '0x7a3b...4f2e' }, created_at: '2026-03-06T09:15:00Z', shipping: { method: 'Standard', address: '456 Oak Ave, Austin, TX 78701' } },
        { id: 'ORD-2026-003', customer: { name: 'Mike Johnson', email: 'mike@test.com' }, status: 'shipped', total: 34.99, items: [{ title: 'Bamboo Cutting Board', qty: 1, price: 34.99 }], payment: { method: 'card', last4: '1234' }, created_at: '2026-03-05T14:20:00Z', tracking: 'UPS-1Z999AA10123456784', shipping: { method: 'Standard', address: '789 Pine Rd, Denver, CO 80202' } },
        { id: 'ORD-2026-004', customer: { name: 'Lisa Wang', email: 'lisa@shop.com' }, status: 'delivered', total: 599.99, items: [{ title: 'Smart Home Hub Pro', qty: 3, price: 199.99 }], payment: { method: 'card', last4: '5678' }, created_at: '2026-03-03T08:00:00Z', shipping: { method: 'Express', address: '321 Elm St, Seattle, WA 98101' } },
        { id: 'ORD-2026-005', customer: { name: 'OpenClaw Bot #42', email: null }, status: 'cancelled', total: 29.99, items: [{ title: 'Coffee Blend', qty: 1, price: 29.99 }], payment: { method: 'usdc', wallet: '0x3e1a...8c5d' }, created_at: '2026-03-04T16:45:00Z', cancel_reason: 'Agent wallet insufficient balance' },
      ]);
      setStats({ total: 5, pending: 1, fulfilled: 2, revenue: 1004.94, today: 2 });
    } finally {
      setLoading(false);
    }
  };

  const cancelOrder = async (orderId) => {
    try {
      await apiClient.post(`/api/orders/${orderId}/cancel`);
      toast.success('Order cancelled');
      loadData();
      setSelectedOrder(null);
    } catch (err) { toast.error('Cancel failed'); }
  };

  const refundOrder = async (orderId) => {
    try {
      await apiClient.post(`/api/orders/${orderId}/refund`);
      toast.success('Refund initiated');
      loadData();
    } catch (err) { toast.error('Refund failed'); }
  };

  const addNote = async (orderId) => {
    if (!note.trim()) return;
    try {
      await apiClient.post(`/api/orders/${orderId}/notes`, { text: note });
      toast.success('Note added');
      setNote('');
    } catch (err) { toast.error('Failed to add note'); }
  };

  const statusConfig = {
    pending: { color: 'bg-[#FFC857]/10 text-[#FFC857]', icon: Clock },
    processing: { color: 'bg-[#7A5CFF]/10 text-[#7A5CFF]', icon: Package },
    shipped: { color: 'bg-[#3EF1C7]/10 text-[#3EF1C7]', icon: Truck },
    delivered: { color: 'bg-[#3EF1C7]/10 text-[#3EF1C7]', icon: CheckCircle },
    cancelled: { color: 'bg-[#F55454]/10 text-[#F55454]', icon: XCircle },
  };

  const statuses = ['all', 'pending', 'processing', 'shipped', 'delivered', 'cancelled'];

  const filtered = orders.filter(o => {
    if (search && !o.id.toLowerCase().includes(search.toLowerCase()) && !o.customer.name.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  return (
    <div className="min-h-screen" style={{ background: '#0B1120' }}>
      <header className="border-b border-[#22304A] bg-[#0E1426]/80 backdrop-blur">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">Order Management</h1>
            <p className="text-sm text-[#9CB0C7]">Order Management</p>
          </div>
          <button onClick={() => navigate('/dashboard')} className="px-4 py-2 border border-[#22304A] text-[#9CB0C7] rounded-lg hover:text-white transition">Dashboard</button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats */}
        <div className="grid grid-cols-5 gap-4 mb-6">
          {[
            { label: 'Total Orders', value: stats.total, icon: ShoppingCart, color: '#7A5CFF' },
            { label: 'Today', value: stats.today, icon: Clock, color: '#FFC857' },
            { label: 'Pending', value: stats.pending, icon: Package, color: '#FFC857' },
            { label: 'Fulfilled', value: stats.fulfilled, icon: CheckCircle, color: '#3EF1C7' },
            { label: 'Revenue', value: `$${stats.revenue.toLocaleString()}`, icon: DollarSign, color: '#3EF1C7' },
          ].map((s, i) => (
            <div key={i} className="bg-[#0E1426] border border-[#22304A] rounded-xl p-4">
              <s.icon className="w-4 h-4 mb-2" style={{ color: s.color }} />
              <div className="text-xl font-bold text-white">{s.value}</div>
              <div className="text-xs text-[#9CB0C7]">{s.label}</div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="flex items-center gap-3 mb-6">
          <div className="flex-1 relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#9CB0C7]" />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search orders..." className="w-full bg-[#0E1426] border border-[#22304A] rounded-lg pl-10 pr-4 py-2.5 text-white text-sm" />
          </div>
          <div className="flex border border-[#22304A] rounded-lg overflow-hidden">
            {statuses.map(s => (
              <button key={s} onClick={() => setStatusFilter(s)} className={`px-3 py-2 text-xs font-medium transition ${statusFilter === s ? 'bg-[#7A5CFF]/20 text-[#7A5CFF]' : 'text-[#9CB0C7] hover:text-white'}`}>
                {s.charAt(0).toUpperCase() + s.slice(1)}
              </button>
            ))}
          </div>
        </div>

        <div className="flex gap-6">
          {/* Orders Table */}
          <div className="flex-1 bg-[#0E1426] border border-[#22304A] rounded-xl overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[#22304A]">
                  <th className="text-left p-3 text-xs text-[#9CB0C7] font-medium">Order</th>
                  <th className="text-left p-3 text-xs text-[#9CB0C7] font-medium">Customer</th>
                  <th className="text-left p-3 text-xs text-[#9CB0C7] font-medium">Status</th>
                  <th className="text-left p-3 text-xs text-[#9CB0C7] font-medium">Payment</th>
                  <th className="text-right p-3 text-xs text-[#9CB0C7] font-medium">Total</th>
                  <th className="text-right p-3 text-xs text-[#9CB0C7] font-medium">Date</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(o => {
                  const sc = statusConfig[o.status] || statusConfig.pending;
                  return (
                    <tr key={o.id} onClick={() => setSelectedOrder(o)} className="border-b border-[#22304A]/50 cursor-pointer hover:bg-[#7A5CFF]/5 transition">
                      <td className="p-3 text-sm text-white font-mono">{o.id}</td>
                      <td className="p-3">
                        <div className="text-sm text-white">{o.customer.name}</div>
                        <div className="text-xs text-[#9CB0C7]">{o.customer.email || 'Agent'}</div>
                      </td>
                      <td className="p-3"><span className={`px-2 py-1 rounded text-xs font-medium ${sc.color}`}>{o.status}</span></td>
                      <td className="p-3">
                        <span className={`text-xs ${o.payment.method === 'usdc' ? 'text-[#FFC857]' : 'text-[#9CB0C7]'}`}>
                          {o.payment.method === 'usdc' ? `USDC ${o.payment.wallet}` : `Card ****${o.payment.last4}`}
                        </span>
                      </td>
                      <td className="p-3 text-sm text-white text-right font-medium">${o.total}</td>
                      <td className="p-3 text-xs text-[#9CB0C7] text-right">{new Date(o.created_at).toLocaleDateString()}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Order Detail Panel */}
          {selectedOrder && (
            <div className="w-96 bg-[#0E1426] border border-[#22304A] rounded-xl p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-white font-bold">{selectedOrder.id}</h3>
                <button onClick={() => setSelectedOrder(null)} className="text-[#9CB0C7] hover:text-white"><X className="w-4 h-4" /></button>
              </div>
              <div className={`px-2 py-1 rounded text-xs font-medium inline-block mb-4 ${statusConfig[selectedOrder.status]?.color}`}>{selectedOrder.status}</div>

              <div className="space-y-4">
                <div>
                  <span className="text-xs text-[#9CB0C7] block mb-1">Customer</span>
                  <span className="text-sm text-white">{selectedOrder.customer.name}</span>
                </div>
                <div>
                  <span className="text-xs text-[#9CB0C7] block mb-1">Items</span>
                  {selectedOrder.items.map((item, i) => (
                    <div key={i} className="flex justify-between text-sm mb-1">
                      <span className="text-white">{item.qty}x {item.title}</span>
                      <span className="text-[#9CB0C7]">${item.price}</span>
                    </div>
                  ))}
                  <div className="flex justify-between text-sm font-bold mt-2 pt-2 border-t border-[#22304A]">
                    <span className="text-white">Total</span>
                    <span className="text-[#3EF1C7]">${selectedOrder.total}</span>
                  </div>
                </div>
                <div>
                  <span className="text-xs text-[#9CB0C7] block mb-1">Shipping</span>
                  <span className="text-sm text-white">{selectedOrder.shipping?.address}</span>
                  {selectedOrder.tracking && <p className="text-xs text-[#7A5CFF] mt-1">Tracking: {selectedOrder.tracking}</p>}
                </div>

                {/* Add Note */}
                <div>
                  <span className="text-xs text-[#9CB0C7] block mb-1">Internal Note</span>
                  <div className="flex gap-2">
                    <input value={note} onChange={e => setNote(e.target.value)} placeholder="Add a note..." className="flex-1 bg-[#0B1120] border border-[#22304A] rounded px-2 py-1.5 text-white text-sm" />
                    <button onClick={() => addNote(selectedOrder.id)} className="px-2 py-1.5 bg-[#7A5CFF] text-white rounded text-sm"><MessageSquare className="w-3 h-3" /></button>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-2 pt-2">
                  {['pending', 'processing'].includes(selectedOrder.status) && (
                    <button onClick={() => navigate('/dashboard/fulfillment')} className="flex-1 py-2 bg-[#3EF1C7]/10 text-[#3EF1C7] rounded-lg text-sm font-medium">Fulfill</button>
                  )}
                  {['pending', 'processing'].includes(selectedOrder.status) && (
                    <button onClick={() => cancelOrder(selectedOrder.id)} className="flex-1 py-2 bg-[#F55454]/10 text-[#F55454] rounded-lg text-sm font-medium">Cancel</button>
                  )}
                  {['delivered', 'shipped'].includes(selectedOrder.status) && (
                    <button onClick={() => refundOrder(selectedOrder.id)} className="flex-1 py-2 bg-[#FFC857]/10 text-[#FFC857] rounded-lg text-sm font-medium flex items-center justify-center gap-1"><RotateCcw className="w-3 h-3" /> Refund</button>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
